import { useState } from 'react';
import type { StorageSasGrant } from '../../../../../types/azureStorage';
import { Button } from '../../../../../components/ui/button';
import { writeClipboardText } from '../../../../../lib/clipboard';
import { trackStorageEvent } from '../../services/storageAnalytics';
import { addSasAsConnection } from '../services/addSasAsConnection';
import { sasServiceNames } from './sasForm';
import { formatStorageDateTime, storageDateTimeUtc } from '../../utils/storageDateTime';
import { sasResourceKindOf } from '../../../../../core/engines/azureStorageSasParameters';
import { sasPermissionName } from '../../../../../core/engines/azureStorageSasSummary';
import { permissionDisplayOrder } from './SasPermissionFields';

function source(grant: StorageSasGrant, field: 'permissions' | 'startsOn' | 'expiresOn'): string {
  if (grant.mode !== 'policy') return 'set by the token';
  if (grant.enforced.supplemented?.includes(field)) return 'set by the token';
  return grant.enforced[field] ? 'from the policy' : 'not set by the policy';
}

function permissionNames(grant: StorageSasGrant): string | undefined {
  const letters = grant.enforced.permissions;
  if (!letters) return undefined;
  const kind = grant.kind === 'account' ? 'account' : sasResourceKindOf(grant.resource) ?? 'blob';
  return `${permissionDisplayOrder(letters).map((letter) => sasPermissionName(kind, letter)).join(', ')} (${letters})`;
}

export function SasResultPanel({ grant }: { grant: StorageSasGrant }) {
  const [revealed, setRevealed] = useState(false);
  // A copy is a side effect: tracked once the clipboard write succeeded, never on a failed write.
  const copy = (action: 'copy_url' | 'copy_query' | 'copy_connection_string', value: string) => {
    void writeClipboardText(value).then((ok) => { if (ok) trackStorageEvent('feature:storage_sas_output', { action }); });
  };
  // U11 / U19: dates in the one storage format, the exact UTC instant as the tooltip.
  // C1 (N11): the permissions by name ("Read, List (rl)"), not the bare letters.
  const rows = [
    ['Permissions', permissionNames(grant), source(grant, 'permissions')],
    ['Start', formatStorageDateTime(grant.enforced.startsOn), source(grant, 'startsOn'), storageDateTimeUtc(grant.enforced.startsOn)],
    ['Expiry', formatStorageDateTime(grant.enforced.expiresOn), source(grant, 'expiresOn'), storageDateTimeUtc(grant.enforced.expiresOn)],
    ['Identifier', grant.enforced.identifier, grant.mode === 'policy' ? 'from the policy' : 'not set'],
    ['Services', grant.enforced.services ? sasServiceNames(grant.enforced.services) : undefined, 'set by the token'],
    ['Resource types', grant.enforced.resourceTypes, 'set by the token'],
    ['Signed object', grant.enforced.signedObjectId, 'from user delegation'],
  ].filter((row) => row[1] || (grant.mode === 'policy' && ['Permissions', 'Start', 'Expiry'].includes(row[0] ?? '')));
  // U19: one labelled SAS URL field with Reveal and the ONE primary action (Copy URL) beside it; the other outputs are
  // outline buttons in one row, the URL-only hint under them.
  return <div className="space-y-3" data-testid="storage-sas-result">
    <div className="space-y-1 text-xs">{rows.map(([label, value, origin, utc]) => <div key={label} title={utc}>{label}: {value || '—'} · {origin}</div>)}</div>
    <div className="space-y-1">
      <label className="text-xs font-medium" htmlFor="storage-sas-url">SAS URL</label>
      <div className="flex items-start gap-2">
        <pre id="storage-sas-url" aria-label="SAS URL" className="max-h-28 min-w-0 flex-1 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 text-xs" data-testid="storage-sas-url">{revealed ? grant.url : '••••••••••••••••'}</pre>
        {!revealed && <Button type="button" size="xs" variant="outline" onClick={() => { setRevealed(true); trackStorageEvent('feature:storage_sas_output', { action: 'reveal' }); }}>Reveal</Button>}
        <Button type="button" size="xs" onClick={() => copy('copy_url', grant.url)}>Copy URL</Button>
      </div>
    </div>
    <div className="flex flex-wrap gap-2">
      <Button type="button" size="xs" variant="outline" onClick={() => copy('copy_query', grant.queryString)}>Copy query string</Button>
      {grant.connectionString && <Button type="button" size="xs" variant="outline" onClick={() => copy('copy_connection_string', grant.connectionString!)}>Copy connection string</Button>}
      <Button type="button" size="xs" variant="outline" className="whitespace-nowrap" onClick={() => { addSasAsConnection(grant); trackStorageEvent('feature:storage_sas_output', { action: 'add_connection' }); }}>Add as a Jam connection…</Button>
    </div>
    {!grant.connectionString && <p className="text-xs text-muted-foreground">A resource SAS travels as a URL.</p>}
  </div>;
}
